import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
import { CustomButton } from "../../../atoms/CustomButton";
import { useUpdateAvatar } from "../../../../hooks/cdn";
import { useUpdateEntityPreferencesByPath } from "../../../../hooks/entity";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

export type ServiceAvatarUpdateTypes = {
  data: any;
  user_identification: string;
  entity_identification: string;
  onChangeNewLogo: (path: string) => void;
};

export const umdBase64 =
  "https://cdn.1cdn.app/application/UMD/22103009493575_SUM_defaul_logo_entities.png";

const ServiceAvatarUpdate = ({
  data,
  entity_identification,
  user_identification,
}: ServiceAvatarUpdateTypes) => {
  const [t] = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.ENTITY_SETTINGS, Level.WRITE);
  const inputFileRef = useRef<any>(null);
  const { mutate, isLoading: isUpdatingAvatar } = useUpdateAvatar();
  const {
    mutate: updatePreferences,
    isLoading: isUpdatingPreferences,
    variables,
  } = useUpdateEntityPreferencesByPath();

  const handleTriggerFileInput = () => {
    let inputFileElement = document.getElementById("avatarInput");

    inputFileRef.current.value = "";

    inputFileElement?.click();
  };

  const handleUploadAvatar = (files: any) => {
    if (files.length > 0) {
      if (files[0].size > 3 * 1048576)
        return toast.error(t("imageUpload.tooLarge", { max: 3 }) as string);

      mutate(
        {
          user_identification: user_identification,
          entity_identification: entity_identification,
          data: { image: files[0] } as any,
        },
        {
          onSuccess: (res: any) => {
            updatePreferences({
              user_identification: user_identification,
              entity_identification: entity_identification,
              path: "cdn_logo_project_path",
              value: res?.data?.element[0]?.cdnPath,
            });

            inputFileRef.current.value = "";
          },
        }
      );
    }
  };

  const handleDeleteServiceAvatar = () => {
    updatePreferences({
      user_identification: user_identification,
      entity_identification: entity_identification,
      path: "cdn_logo_project_path",
      value: umdBase64,
    });
  };

  return (
    <div className="flex flex-col items-start gap-2" id="input-logo">
      <p className="text-sm font-medium text-gray-700">
        {t("dashboard.develop.serviceAvatarUpdate.logo")}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-12 w-12 overflow-hidden rounded-md border border-gray-200 bg-gray-100">
          <img
            loading="lazy"
            className="h-12 w-12 object-cover"
            src={data?.cdn_logo_project_path}
            alt=""
          />
        </div>
        {canWrite && (
          <CustomButton
            variant="secondary"
            size="sm"
            onClick={handleTriggerFileInput}
            isLoading={isUpdatingAvatar}
            title={t("dashboard.develop.serviceAvatarUpdate.btn-01")}
          />
        )}
        {canWrite && data?.cdn_logo_project_path !== umdBase64 && (
          <CustomButton
            variant="secondary"
            size="sm"
            onClick={handleDeleteServiceAvatar}
            isLoading={
              isUpdatingPreferences &&
              variables?.data?.cdn_logo_project_path === umdBase64
            }
            title={t("common.delete")}
          />
        )}
      </div>
      {canWrite && (
        <input
          type={"file"}
          hidden
          id="avatarInput"
          accept=".jpg,.png,.jpeg"
          onChange={(e) => handleUploadAvatar(e.target.files)}
          ref={inputFileRef}
        />
      )}
    </div>
  );
};

export default ServiceAvatarUpdate;
